import { qs, qsa, listen } from "@fluorescent/dom";

import { section } from "@/glow";
import getMediaQuery from "@/lib/media-queries";
import atBreakpointChange from "@/lib/at-breakpoint-change";
import Disclosure from "@/lib/disclosure";
import Navigation from "@/lib/announcement-bar/navigation";
import { emit, on } from "@/glow/events";
import dispatchCustomEvent from "@/lib/dispatch-custom-event";

const selectors = {
  mobileOnlyInner: ".announcement-bar__item-inner-mobile-only",
  desktopOnlyInner: ".announcement-bar__item-inner-desktop-only",
  disclosure: "[data-disclosure]",
  navigation: "[data-navigation]",
  moreDetailsLink: ".announcement-bar__details-link",
};

section("announcement-bar", {
  setHeightVariable() {
    if (this.container.offsetHeight !== this.lastSetHeight) {
      document.documentElement.style.setProperty(
        "--announcement-height",
        `${this.container.offsetHeight}px`
      );
      this.lastSetHeight = this.container.offsetHeight;
    }
  },

  onLoad() {
    const { scrollerId } = this.container.dataset;
    this.scrollerId = scrollerId;
    this.setHeightVariable();
    const moreDetailsTriggers = qsa(selectors.moreDetailsLink, this.container);
    this.scrollerInitialized = false;

    document.addEventListener(
      `scroll-slider-${this.scrollerId}:initialized`,
      () => {
        this.scrollerInitialized = true;
      },
      { once: true }
    );

    this.events = [
      listen(moreDetailsTriggers, "click", e => {
        e.preventDefault();
        e.stopPropagation();

        const { contentTargetId } = e.target.dataset;
        const content = qs(`#${contentTargetId}`, this.container);
        const container = document.createElement("div");
        container.innerHTML = content.innerHTML;

        emit("modal:open", null, {
          modalContent: container,
          narrow: true,
        });
      }),
      on("window:resized", () => {
        this.setHeightVariable();
      }),
    ];

    this.disableTabbingToInners = function () {
      // Disable tabbing on items that aren't shown
      const desktopOnlyInners = qsa(selectors.desktopOnlyInner, this.container);
      const mobileOnlyInners = qsa(selectors.mobileOnlyInner, this.container);
      const desktopIsMobileSize = window.matchMedia(
        getMediaQuery("below-720")
      ).matches;

      desktopOnlyInners.forEach(inner => {
        inner.toggleAttribute("inert", desktopIsMobileSize);
      });

      mobileOnlyInners.forEach(inner => {
        inner.toggleAttribute("inert", !desktopIsMobileSize);
      });
    };

    this.disableTabbingToInners();
    this.breakPointHandler = atBreakpointChange(720, () => {
      this.disableTabbingToInners();
    });
    this.crossBorder = {};

    this.navigation = Navigation(
      qs(selectors.navigation, this.container),
      this
    );

    // Wire up Cross Border disclosures
    const cbSelectors = qsa(selectors.disclosure, this.container);

    if (cbSelectors) {
      cbSelectors.forEach(selector => {
        const { disclosure: d } = selector.dataset;
        this.crossBorder[d] = Disclosure(selector);
      });
    }

    // In order to place the account component with the account icon correctly, we need to know the "visible"
    // height of the announcement bar.
    this.intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.intersectionRatio == 1.0) {
            document.documentElement.style.setProperty(
              "--visible-announcement-height",
              `${this.lastSetHeight}px`
            );
          } else {
            document.documentElement.style.setProperty(
              "--visible-announcement-height",
              `${Math.max(0, this.lastSetHeight - window.scrollY)}px`
            );
          }
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1.0] }
    );

    this.intersectionObserver.observe(this.container);
  },

  handleBlockSelect(index, behavior) {
    dispatchCustomEvent(`scroll-slider-${this.scrollerId}:pause`);
    dispatchCustomEvent(`scroll-slider-${this.scrollerId}:go-to-slide`, {
      slideIndex: index,
      behavior,
    });
  },

  onBlockSelect({ target: slide }) {
    const index = parseInt(slide.dataset.index, 10);

    // When the editor flushes the section on block select
    // we need to wait for the scroll-slider to initialize
    // before we can scroll to the selected slide. The editor
    // will flush section content on setting update, but will
    // not on selecting the block itself.
    if (this.scrollerInitialized) {
      this.handleBlockSelect(index, "instant");
    } else {
      document.addEventListener(
        `scroll-slider-${this.scrollerId}:initialized`,
        () => {
          this.handleBlockSelect(index, "instant");
        },
        { once: true }
      );
    }
  },

  onBlockDeselect() {
    dispatchCustomEvent(`scroll-slider-${this.scrollerId}:play`);
  },

  onUnload() {
    Object.keys(this.crossBorder).forEach(t => this.crossBorder[t].unload());
    this.intersectionObserver.disconnect();
  },
});
